import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { addBreadcrumb } from "@/lib/logger";
import { Message } from "@/types/message";

interface UseMessageRealtimeProps {
  userId: string | null;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  checkIfAtBottom: () => boolean;
  scrollToBottom: () => void;
  setHasNewMessages: (value: boolean) => void;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
}

export const useMessageRealtime = ({
  userId,
  setMessages,
  checkIfAtBottom,
  scrollToBottom,
  setHasNewMessages,
  messagesContainerRef,
}: UseMessageRealtimeProps) => {
  const updateDebounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!userId) return;
    
    addBreadcrumb('Subscribing to realtime messages', { user_id: userId }, 'info', 'realtime');
    
    const channel = supabase
      .channel(`messages:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const newMessage = payload.new as Message;
          console.log('[REALTIME] INSERT event received:', { 
            id: newMessage.id, 
            role: newMessage.role,
          });
          
          addBreadcrumb('Realtime message INSERT', {
            message_id: newMessage.id,
            role: newMessage.role,
          }, 'info', 'realtime');
          
          setMessages((prev) => {
            if (prev.some(m => m.id === newMessage.id)) {
              console.log('[REALTIME] Message already exists, skipping');
              return prev;
            }
            console.log('[REALTIME] Adding new message to state');
            return [...prev, newMessage];
          });
          
          const atBottom = checkIfAtBottom();
          console.log('[REALTIME] After message insert, atBottom:', atBottom);
          
          if (atBottom) {
            console.log('[REALTIME] User at bottom, auto-scrolling');
            setTimeout(() => scrollToBottom(), 100);
          } else {
            console.log('[REALTIME] User NOT at bottom, showing button');
            setHasNewMessages(true);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "messages",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const updatedMessage = payload.new as Message;
          
          const container = messagesContainerRef.current;
          const scrollTop = container?.scrollTop || 0;
          const scrollHeight = container?.scrollHeight || 0;
          
          if (updateDebounceRef.current) {
            clearTimeout(updateDebounceRef.current);
          }
          
          updateDebounceRef.current = setTimeout(() => {
            setMessages((prev) => {
              if (updatedMessage.deleted) {
                return prev.filter(m => m.id !== updatedMessage.id);
              }
              return prev.map(m => m.id === updatedMessage.id ? updatedMessage : m);
            });
            
            requestAnimationFrame(() => {
              if (container) {
                const newScrollHeight = container.scrollHeight;
                const heightDiff = newScrollHeight - scrollHeight;
                container.scrollTop = scrollTop + heightDiff;
              }
            });
          }, 50);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "messages",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const deletedMessage = payload.old as Message;
          setMessages((prev) => prev.filter(m => m.id !== deletedMessage.id));
        }
      )
      .subscribe();

    return () => {
      if (updateDebounceRef.current) {
        clearTimeout(updateDebounceRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [userId, setMessages, checkIfAtBottom, scrollToBottom, setHasNewMessages, messagesContainerRef]);
};
